import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import GlobalErrorFlashModal from "../../../global/GlobalErrorFlashModal";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import { getAuthItem } from "../../../public/utils/authStorage";
import { getJwtRoleSlug } from "../../../public/utils/jwtRole";
import { getMyTournamentsOverview } from "../api/studentTournamentApi";
import { EMPTY_MY_TOURNAMENTS_OVERVIEW } from "../utils/myTournamentsConstants";
import MyTournamentsDashboardSection from "../components/my-tournaments/MyTournamentsDashboardSection";
import MyTournamentsSummarySection from "../components/my-tournaments/MyTournamentsSummarySection";
import MyTournamentsDirectorySection from "../components/my-tournaments/MyTournamentsDirectorySection";
import MyTournamentsTimelineSection from "../components/my-tournaments/MyTournamentsTimelineSection";
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";
import "../../memberships/styles/MyMembershipsPage.css";
import "../styles/MyTournamentsPage.css";

function readAuthState() {
  const token = getAuthItem("jwt_token");
  return {
    hasToken: Boolean(token),
    roleSlug: token ? getJwtRoleSlug(token) || "" : "",
  };
}

export default function MyTournamentsPage() {
  const [auth, setAuth] = useState(readAuthState);
  const [overview, setOverview] = useState(EMPTY_MY_TOURNAMENTS_OVERVIEW);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState({ message: "", title: "Notice" });
  const [success, setSuccess] = useState("");

  const clearNotice = () => setNotice({ message: "", title: "Notice" });

  const handleFeedback = ({ type, text, title }) => {
    if (type === "success") {
      setSuccess(text);
      clearNotice();
      return;
    }
    setSuccess("");
    setNotice({
      message: text,
      title: title || "Notice",
    });
  };

  const isStudent = auth.roleSlug === "students";
  const showStudentLoginGate = auth.hasToken && !isStudent;

  useEffect(() => {
    setAuth(readAuthState());
  }, []);

  useEffect(() => {
    if (showStudentLoginGate || !auth.hasToken) {
      setOverview(EMPTY_MY_TOURNAMENTS_OVERVIEW);
      setLoading(false);
      clearNotice();
      return;
    }

    let alive = true;
    (async () => {
      setLoading(true);
      clearNotice();
      try {
        const res = await getMyTournamentsOverview();
        if (!alive) return;
        setOverview(res?.data || EMPTY_MY_TOURNAMENTS_OVERVIEW);
      } catch (err) {
        if (!alive) return;
        if (err?.code === "STUDENT_ROLE_REQUIRED") {
          setOverview(EMPTY_MY_TOURNAMENTS_OVERVIEW);
          clearNotice();
          return;
        }
        setNotice({
          message: err?.message || "Failed to load tournament overview",
          title: "Unable to load",
        });
        setOverview(EMPTY_MY_TOURNAMENTS_OVERVIEW);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [auth.hasToken, isStudent, showStudentLoginGate]);

  const studentName = useMemo(
    () => overview?.student?.full_name || "",
    [overview?.student?.full_name]
  );

  if (showStudentLoginGate) {
    return (
      <div className="app-screen-guard app-screen-guard--blocked">
        <h2 className="app-screen-guard__title">Student login required</h2>
        <p className="app-screen-guard__text">
          You must login as student to experience this page.
        </p>
        <div className="app-screen-guard__actions">
          <Link to="/student-login" className="app-screen-guard__link">
            Go to student login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-shell">
      <header className="mt-hero">
        <p className="mt-hero__eyebrow">Student portal</p>
        <h1 className="mt-hero__title">My tournaments</h1>
        <p className="mt-hero__sub">
          {auth.hasToken && isStudent && studentName
            ? `Analyse tournament activity for ${studentName}. This screen is read-only.`
            : "Analyse your tournament registrations and results. Sign in as a student to load your records."}
        </p>
      </header>

      <GlobalSuccessFlashModal message={success} onDismiss={() => setSuccess("")} />
      <GlobalErrorFlashModal
        message={notice.message}
        title={notice.title}
        onDismiss={clearNotice}
      />

      <MyTournamentsDashboardSection
        dashboard={overview.dashboard}
        loading={loading && auth.hasToken}
      />

      <MyTournamentsSummarySection
        summary={overview.summary}
        directory={overview.directory}
        loading={loading && auth.hasToken}
      />

      <MyTournamentsDirectorySection
        directory={overview.directory}
        loading={loading && auth.hasToken}
        onFeedback={handleFeedback}
      />

      <MyTournamentsTimelineSection
        timeline={overview.timeline}
        loading={loading && auth.hasToken}
      />
    </div>
  );
}
